// ============================================================
// MongoDBMongoose.jsx
//
// A branded, technical guide to Mongoose ODM (Object Document Mapper).
// Covers Connection, Schemas, Models, and Validation.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #880000  (Mongoose Crimson/Purple)
//   Accent      → #00ED64  (MongoDB Green)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Alert,
    Button
} from "@mui/material";

/* MUI Icons */
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import SchemaIcon from "@mui/icons-material/Schema";
import RuleIcon from "@mui/icons-material/Rule";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import HubIcon from "@mui/icons-material/Hub";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(136, 0, 0, 0.3)", // Mongoose Crimson
    borderHover: "rgba(0, 237, 100, 0.5)",
    crimson: "#FF4D4D",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.crimson }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#E0E7FF", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBMongoose() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.crimson }} />}
                        label="Application Layer · Module 18"
                        sx={{ bgcolor: "rgba(255,77,77,0.1)", color: BRAND.crimson, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Mongoose <span style={{ color: BRAND.crimson }}>ODM</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Bring structure to the flexible. <strong>Mongoose</strong> is an Object Document Mapper (ODM) for Node.js
                        that provides a straight-forward, schema-based solution to model your application data.
                    </Typography>
                </Box>

                {/* 2. THE CONNECTION */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <SettingsEthernetIcon sx={{ color: BRAND.crimson, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Establishing the Link</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Mongoose acts as the bridge between your JavaScript logic and the MongoDB server.
                            It manages a <strong>connection pool</strong> automatically, so you don't have to manually open
                            and close connections for every query.
                        </Typography>
                        <Alert severity="warning" sx={{ bgcolor: "rgba(255,77,77,0.05)", color: BRAND.white, border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="body2">
                                Always use <strong>Environment Variables</strong> for your Connection URI to keep your database credentials secure.
                            </Typography>
                        </Alert>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal
                            label="dbConnect.js"
                            code={`const mongoose = require('mongoose');\n\nmongoose.connect(process.env.MONGO_URI)\n  .then(() => console.log('Connected to MongoDB'))\n  .catch(err => console.error('Connection Error', err));`}
                        />
                    </Grid>
                </Grid>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. SCHEMAS & MODELS */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <SchemaIcon sx={{ color: BRAND.crimson, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Defining the Contract</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        While MongoDB is "schemaless," your application shouldn't be. A <strong>Schema</strong> defines
                        the structure of your documents, while a <strong>Model</strong> provides the interface for CRUD operations.
                    </Typography>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={7}>
                            <CodeTerminal
                                label="User.js (Model Definition)"
                                code={`const userSchema = new mongoose.Schema({\n  username: { type: String, required: true, unique: true },\n  email: { type: String, lowercase: true },\n  age: { type: Number, min: 18 },\n  createdAt: { type: Date, default: Date.now }\n});\n\nmodule.exports = mongoose.model('User', userSchema);`}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Stack spacing={3}>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="h6" sx={{ color: BRAND.crimson, fontWeight: 800 }}>Built-in Validation</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                        Mongoose checks your data <strong>before</strong> it reaches the database.
                                        If an <code>age</code> is under 18, it throws an error immediately.
                                    </Typography>
                                </Paper>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="h6" sx={{ color: BRAND.green, fontWeight: 800 }}>Defaults & Casts</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                        It automatically handles data type casting (e.g., converting a string "25" to a Number)
                                        and populates default values like <code>Date.now</code>.
                                    </Typography>
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>



                {/* 4. QUERYING WITH PROMISES */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(255, 77, 77, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <HubIcon sx={{ color: BRAND.crimson }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The Mongoose API</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        Mongoose queries are <strong>Thenables</strong>. You can use modern <code>async/await</code> syntax
                        to write clean, readable database logic.
                    </Typography>

                    <CodeTerminal
                        label="userController.js"
                        code={`// Finding a user and updating in one flow\nconst updateLogin = async (userId) => {\n  const user = await User.findByIdAndUpdate(\n    userId, \n    { lastLogin: Date.now() },\n    { new: true } // Returns the updated document\n  );\n  return user;\n};`}
                    />
                </Box>

                {/* 5. PRO-TIP: MIDDLEWARE */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={2}>
                            <Avatar sx={{ bgcolor: BRAND.crimson }}><RuleIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>Pre-Save Hooks</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                    One of Mongoose's most powerful features is <strong>Middleware</strong>.
                                    You can use <code>schema.pre('save')</code> to hash passwords or generate slugs
                                    automatically every time a document is created or updated.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #440000)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <StorageIcon style={{ fontSize: 40, color: BRAND.crimson, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Application Ready?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        You've connected the hardware and the software. Now it's time to build your full-stack capstone project.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.crimson, color: BRAND.white, fontWeight: 900 }}>Go to Final Project</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Mongoose Documentation</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
